import { useState, useEffect } from 'react';
import { Checkbox } from '../../../../components/ui/checkbox';
import { Label } from '../../../../components/ui/label';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import {
  useAppStore,
  defaultQueryExecutionSettings,
  type KeywordCasingMode,
  type AutocompleteVersionFilter,
} from '../../../../store/appStore';
import { pgsqlCheckTools } from '../../../backup-restore/services/backupRestoreApi';
import { CheckCircle2, XCircle } from 'lucide-react';
import { useAnalytics } from '@/core/analytics/useAnalytics';

import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
export function AdvancedSettingsTab() {
  const { track } = useAnalytics();
  const intellisenseSettings = useAppStore((state) => state.intellisenseSettings);
  const setIntellisenseSettings = useAppStore((state) => state.setIntellisenseSettings);
  const pgsqlToolsSettings = useAppStore((state) => state.pgsqlToolsSettings);
  const setPgsqlToolsSettings = useAppStore((state) => state.setPgsqlToolsSettings);
  const queryExecutionSettings = useAppStore((state) => state.queryExecutionSettings);
  const setQueryExecutionSettings = useAppStore((state) => state.setQueryExecutionSettings);
  const resetQueryExecutionSettings = useAppStore((state) => state.resetQueryExecutionSettings);

  // Local state for timeout input (Save/Cancel pattern)
  const [localTimeout, setLocalTimeout] = useState(queryExecutionSettings.timeoutSeconds);
  const isTimeoutDirty = localTimeout !== queryExecutionSettings.timeoutSeconds;

  // Sync local state when store changes externally (e.g. reset to defaults)
  useEffect(() => {
    setLocalTimeout(queryExecutionSettings.timeoutSeconds);
  }, [queryExecutionSettings.timeoutSeconds]);

  // PostgreSQL tools status
  const [pgToolsStatus, setPgToolsStatus] = useState<{
    checking: boolean;
    available: boolean;
    path?: string;
    version?: string;
    error?: string;
  }>({ checking: true, available: false });

  // Check PostgreSQL tools availability
  useEffect(() => {
    const checkTools = async () => {
      setPgToolsStatus({ checking: true, available: false });
      const customPath = pgsqlToolsSettings.customBinPath?.trim() || undefined;
      const result = await pgsqlCheckTools(customPath);
      setPgToolsStatus({
        checking: false,
        available: result.available,
        path: result.path,
        version: result.version,
        error: result.error,
      });
    };
    checkTools();
  }, [pgsqlToolsSettings.customBinPath]);

  return (
    <div className="space-y-4">
      {/* Query Execution Section */}
      <div className="space-y-3">
        <div className="text-sm font-medium">Query Execution</div>

        <div className="rounded-md border border-border bg-background p-3 space-y-3">
          <div className="space-y-2">
            <Label htmlFor="query-timeout">Query timeout (seconds)</Label>
            <Input
              id="query-timeout"
              data-testid="query-timeout-input"
              type="number"
              min={0}
              step={1}
              value={localTimeout}
              onChange={(e) => {
                const value = parseInt(e.target.value, 10);
                if (!isNaN(value) && value >= 0) {
                  setLocalTimeout(value);
                }
              }}
              className="w-32"
            />
            <div className="text-xs text-muted-foreground">
              Maximum time to wait for a query to complete. Set to 0 for no timeout.
              Default: {defaultQueryExecutionSettings.timeoutSeconds} seconds.
              Applies to queries run from the Query Editor on SQL Server connections.
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              resetQueryExecutionSettings();
              setLocalTimeout(defaultQueryExecutionSettings.timeoutSeconds);
            }}
            data-testid="query-execution-reset-defaults"
          >
            Reset to defaults
          </Button>
          {isTimeoutDirty && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setLocalTimeout(queryExecutionSettings.timeoutSeconds)}
                data-testid="query-execution-cancel"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={() => setQueryExecutionSettings({ timeoutSeconds: localTimeout })}
                data-testid="query-execution-save"
              >
                Save
              </Button>
            </>
          )}
        </div>
      </div>

      {/* SQL Autocomplete (cross-engine) */}
      <div className="space-y-3">
        <div className="text-sm font-medium">SQL autocomplete</div>

        <div
          className="space-y-3 rounded-md border border-border bg-background p-3"
          data-testid="intellisense-settings"
        >
          <div className="flex items-start gap-3">
            <Checkbox
              id="intellisense-enabled"
              data-testid="intellisense-enabled-toggle"
              checked={intellisenseSettings.enabled}
              onCheckedChange={(checked) => {
                const enabled = checked === true;
                setIntellisenseSettings({ enabled });
                track('feature:intellisense_enabled_toggle', { enabled });
              }}
            />
            <div className="grid gap-1">
              <Label htmlFor="intellisense-enabled">Enable IntelliSense</Label>
              <div className="text-xs text-muted-foreground">
                Turn off to disable autocomplete (Ctrl+Space included), hover
                tooltips, signature help, inlay hints, quick fixes and live
                diagnostics in every SQL editor. The options below only apply
                while this is on.
              </div>
            </div>
          </div>

          <div
            className={
              intellisenseSettings.enabled
                ? 'space-y-3 border-t border-border pt-3'
                : 'space-y-3 border-t border-border pt-3 opacity-50 pointer-events-none'
            }
            aria-disabled={!intellisenseSettings.enabled}
            data-testid="intellisense-feature-settings"
          >
          <div className="flex items-start gap-3">
            <Checkbox
              id="auto-suggest"
              data-testid="auto-suggest-toggle"
              checked={intellisenseSettings.autoSuggest}
              onCheckedChange={(checked) => {
                const enabled = checked === true;
                setIntellisenseSettings({ autoSuggest: enabled });
                track('feature:auto_suggest_toggle', { enabled });
              }}
            />
            <div className="grid gap-1">
              <Label htmlFor="auto-suggest">Suggest while typing</Label>
              <div className="text-xs text-muted-foreground">
                Turn off to stop the suggestion popup from opening as you type.
                Ctrl+Space still opens it on demand.
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="keyword-casing">Keyword case on insert</Label>
            <select
              id="keyword-casing"
              data-testid="keyword-casing-select"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={intellisenseSettings.keywordCasing}
              onChange={(e) => {
                const mode = e.target.value as KeywordCasingMode;
                setIntellisenseSettings({ keywordCasing: mode });
                if (mode !== intellisenseSettings.keywordCasing) {
                  track('feature:keyword_casing_mode', { mode });
                }
              }}
            >
              <option value="match-user">Match what you type</option>
              <option value="upper">UPPER CASE</option>
              <option value="lower">lower case</option>
            </select>
            <div className="text-xs text-muted-foreground">
              Controls the case applied to SQL keywords when accepting an
              autocomplete suggestion. <strong>Match what you type</strong>{' '}
              follows the typed prefix and falls back to the dominant case in
              the query.
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="autocomplete-version-filter">
              Version-incompatible suggestions
            </Label>
            <select
              id="autocomplete-version-filter"
              data-testid="autocomplete-version-filter-select"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={intellisenseSettings.autocompleteVersionFilter}
              onChange={(e) => {
                const mode = e.target.value as AutocompleteVersionFilter;
                setIntellisenseSettings({ autocompleteVersionFilter: mode });
                if (mode !== intellisenseSettings.autocompleteVersionFilter) {
                  track('feature:autocomplete_version_filter', { mode });
                }
              }}
            >
              <option value="annotated">Show with badges (recommended)</option>
              <option value="strict">Hide unavailable, badge deprecated</option>
              <option value="off">Show everything, no badges</option>
            </select>
            <div className="text-xs text-muted-foreground">
              How to surface keywords / functions that don't fit your server's
              version. <strong>Show with badges</strong> keeps every
              suggestion visible with a right-aligned tag like{' '}
              <code>≥ 2017</code> and demotes them in ranking.
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Checkbox
              id="type-mismatch-diagnostics"
              data-testid="type-mismatch-diagnostics"
              checked={intellisenseSettings.typeMismatchDiagnostics}
              onCheckedChange={(checked) => {
                const enabled = checked === true;
                setIntellisenseSettings({ typeMismatchDiagnostics: enabled });
                track('feature:type_check_diagnostics_toggle', { enabled });
              }}
            />
            <div className="grid gap-1">
              <Label htmlFor="type-mismatch-diagnostics">
                Type-mismatch warnings
              </Label>
              <div className="text-xs text-muted-foreground">
                Squiggle comparisons that are a type error on your engine — e.g.
                a numeric column compared to non-numeric text
                (<code>WHERE total = 'free'</code>). Only fires with a loaded
                schema and only on mismatches that are a bug on every engine.
              </div>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Checkbox
              id="live-syntax-diagnostics"
              data-testid="live-syntax-diagnostics"
              checked={intellisenseSettings.liveSyntaxDiagnostics}
              onCheckedChange={(checked) => {
                const enabled = checked === true;
                setIntellisenseSettings({ liveSyntaxDiagnostics: enabled });
                track('feature:syntax_diagnostics_toggle', { enabled });
              }}
            />
            <div className="grid gap-1">
              <Label htmlFor="live-syntax-diagnostics">
                Live syntax errors (MS SQL, MySQL, Oracle, SQLite)
              </Label>
              <div className="text-xs text-muted-foreground">
                Squiggle genuine syntax errors as you type. MS SQL checks T-SQL
                against two independent grammars and marks only where both
                agree; MySQL, Oracle, and SQLite pair the engine grammar with an
                independent lexical check (typo'd keywords, unbalanced
                parentheses, dangling operators). Never mid-keystroke — a valid
                statement is never flagged.
              </div>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Checkbox
              id="semantic-type-hover"
              data-testid="semantic-type-hover"
              checked={intellisenseSettings.semanticTypeHover}
              onCheckedChange={(checked) => {
                const enabled = checked === true;
                setIntellisenseSettings({ semanticTypeHover: enabled });
                track('feature:semantic_hover_toggle', { enabled });
              }}
            />
            <div className="grid gap-1">
              <Label htmlFor="semantic-type-hover">
                Type-on-hover
              </Label>
              <div className="text-xs text-muted-foreground">
                Hover a column to see its data type, or a function call
                (<code>UPPER(name)</code>) to see its inferred result type. Reads
                the loaded schema; composes with the engine's own hover.
              </div>
            </div>
          </div>

          <div className="border-t border-border pt-3 space-y-3">
            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Safe mode
            </div>

            <div className="flex items-start gap-3">
              <Checkbox
                id="live-diagnostics"
                data-testid="live-diagnostics-toggle"
                checked={intellisenseSettings.liveDiagnostics}
                onCheckedChange={(checked) => {
                  const enabled = checked === true;
                  setIntellisenseSettings({ liveDiagnostics: enabled });
                  track('feature:live_diagnostics_toggle', { enabled });
                }}
              />
              <div className="grid gap-1">
                <Label htmlFor="live-diagnostics">Live diagnostics</Label>
                <div className="text-xs text-muted-foreground">
                  Turn off to disable every diagnostic marker at once — syntax
                  errors, type-mismatch warnings, and language-server markers.
                  Existing markers are cleared; use if squiggles misbehave.
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Checkbox
                id="hover-sighelp"
                data-testid="hover-sighelp-toggle"
                checked={intellisenseSettings.hoverAndSignatureHelp}
                onCheckedChange={(checked) => {
                  const enabled = checked === true;
                  setIntellisenseSettings({ hoverAndSignatureHelp: enabled });
                  track('feature:hover_sighelp_toggle', { enabled });
                }}
              />
              <div className="grid gap-1">
                <Label htmlFor="hover-sighelp">Hover & signature help</Label>
                <div className="text-xs text-muted-foreground">
                  Turn off to disable every hover tooltip and function
                  signature-help popup at once; use if hover/sig-help misbehaves.
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Checkbox
                id="semantic-completion"
                data-testid="semantic-completion-toggle"
                checked={intellisenseSettings.semanticCompletion}
                onCheckedChange={(checked) => {
                  const enabled = checked === true;
                  setIntellisenseSettings({ semanticCompletion: enabled });
                  track('feature:semantic_completion_toggle', { enabled });
                }}
              />
              <div className="grid gap-1">
                <Label htmlFor="semantic-completion">Semantic completions</Label>
                <div className="text-xs text-muted-foreground">
                  Turn off to skip the schema-aware resolver entirely; falls
                  back to word-based suggestions from the current buffer.
                </div>
              </div>
            </div>
          </div>
          </div>
        </div>
      </div>

      {/* The "MS SQL IntelliSense (SQL Tools Service)" section was REMOVED in
          Plan 20 §10 step 7 (T7 review I3): its five toggles configured SSTS's
          server-side language features, which no longer serve mssql documents
          at all (steps 2–6 moved completion/hover/diagnostics/signature help
          in-house) — the checkboxes persisted state that nothing read. The
          in-house equivalents live in the "SQL autocomplete" section above. */}

      {/* PostgreSQL Tools Section */}
      <div className="space-y-3">
        <div className="text-sm font-medium">PostgreSQL Tools</div>

        <div className="rounded-md border border-border bg-background p-3 space-y-3">
          <div className="text-xs text-muted-foreground">
            Path to PostgreSQL client tools (pg_dump, pg_restore, psql) for backup and restore operations.
          </div>

          <div className="space-y-2">
            <Label htmlFor="pgsql-bin-path">Custom bin path (optional)</Label>
            <Input
              id="pgsql-bin-path"
              data-testid="pgsql-bin-path-input"
              placeholder="e.g., /opt/homebrew/bin or C:\Program Files\PostgreSQL\16\bin"
              value={pgsqlToolsSettings.customBinPath}
              onChange={(e) => setPgsqlToolsSettings({ customBinPath: e.target.value })}
            />
            <div className="text-xs text-muted-foreground">
              Leave empty to use auto-detection. Set if tools are not found automatically.
            </div>
          </div>

          {/* Status indicator */}
          <div className="flex items-center gap-2 text-sm">
            {pgToolsStatus.checking ? (
              <>
                <RandomDotmSpinner size={16} className="text-muted-foreground" />
                <span className="text-muted-foreground">Checking tools availability...</span>
              </>
            ) : pgToolsStatus.available ? (
              <>
                <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                <span className="text-green-700 dark:text-green-300">
                  Tools found: {pgToolsStatus.path}
                </span>
              </>
            ) : (
              <>
                <XCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
                <span className="text-red-700 dark:text-red-300">
                  {pgToolsStatus.error || 'PostgreSQL tools not found'}
                </span>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
